import { TQuestion } from "../../../api/type"
import { GRADES } from "../../../utils/grade"
import { complexities } from "../../../utils/complexity"
import { formatSubPartsLabels } from "../../../utils/subParts"
import {
  DEFAULT_LEVEL_LABELS,
  labelAt,
  normalizeLabels,
} from "../../Subjects/detail/hierarchyUtils"

export const formatQuestionStatus = (status: string) => {
  if (status === "published") return "Published"
  if (status === "archived") return "Archived"
  if (status === "draft") return "Draft"
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export const isUnverifiedQuestion = (item: TQuestion) =>
  item.verification_status === "unverified" ||
  item.verification_status === "rejected"

export const formatVerificationStatus = (status?: string | null) => {
  if (status === "unverified") return "Not verified"
  if (status === "verified") return "Verified"
  if (status === "rejected") return "Rejected"
  return ""
}

export const getQuestionStatusClassName = (status?: string | null) => {
  if (status === "published") return "question-status--published"
  if (status === "archived") return "question-status--archived"
  return "question-status--draft"
}

export const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()

/**
 * Labels of the pieces still missing before a question can be published to
 * learners. Mirrors the backend rule: a field is satisfied when present at the
 * top level, or (for sub-part questions) on every sub-part. Empty = ready.
 */
export const getLearnerPublishBlockers = (item: TQuestion): string[] => {
  const hasText = (value?: string | null) => !!stripHtml(value ?? "")
  const parts = item.sub_parts ?? []
  const fieldReady = (
    topValue: string | null | undefined,
    key: "answer" | "solution" | "marking_scheme"
  ) =>
    hasText(topValue) ||
    (parts.length > 0 && parts.every((part) => hasText(part?.[key])))

  const blockers: string[] = []
  if (!hasText(item.question)) blockers.push("Question")
  if (!fieldReady(item.answer, "answer")) blockers.push("Answer")
  if (!fieldReady(item.solution, "solution")) blockers.push("Solution")
  if (!fieldReady(item.marking_scheme, "marking_scheme"))
    blockers.push("Marking scheme")
  return blockers
}

export const getQuestionMeta = (
  item: TQuestion,
  options?: { subjectLabel?: string | null; levelLabels?: string[] }
) => {
  const gradeLabel =
    GRADES.find((grade) => grade.value === item.grade)?.label || item.grade
  const complexityLabel =
    complexities.find((level) => level.value === item.complexity)?.label ||
    item.complexity
  const subjectLabel =
    options?.subjectLabel?.trim() || item.subject_title?.trim() || null

  // Render each populated hierarchy level using the subject's configurable
  // labels (falling back to the defaults when none are supplied).
  const labels = normalizeLabels(
    options?.levelLabels && options.levelLabels.length
      ? options.levelLabels
      : DEFAULT_LEVEL_LABELS
  )
  const levelNames = [
    item.topic,
    item.level_2_name,
    item.level_3_name,
    item.level_4_name,
  ]
  const levelRows = levelNames.map((name, idx) =>
    name ? { label: labelAt(labels, idx + 1), value: name } : null
  )

  return [
    subjectLabel ? { label: "Subject", value: subjectLabel } : null,
    gradeLabel ? { label: "Level", value: gradeLabel } : null,
    ...levelRows,
    item.year ? { label: "Year", value: String(item.year) } : null,
    item.source ? { label: "Source", value: item.source } : null,
    complexityLabel ? { label: "Difficulty", value: complexityLabel } : null,
    item.marks != null ? { label: "Marks", value: String(item.marks) } : null,
    item.sub_parts?.length
      ? { label: "Sub-parts", value: formatSubPartsLabels(item.sub_parts) }
      : null,
    item.question_status
      ? {
          label: "Status",
          value: formatQuestionStatus(item.question_status),
        }
      : null,
    item.content_types?.length
      ? { label: "Types", value: item.content_types.join(", ") }
      : null,
  ].filter(Boolean) as { label: string; value: string }[]
}
